import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { protocolosKeys } from '@/features/protocolos/hooks/useProtocolos';
import type { EditarCaptionVars, EliminarFotoVars } from '@/features/analisis/common/fotosTypes';

import { sedimentacionFhApi } from '../api';
import type { SedimentacionFh, SedimentacionFhCreate, SedimentacionFhUpdate } from '../types';

const QUERY_KEY_BASE = ['analisis-sedimentacion-fh'] as const;

export const sedimentacionFhKeys = {
  all: QUERY_KEY_BASE,
  byProtocolo: (uidProtocolo: string) =>
    [...QUERY_KEY_BASE, 'by-protocolo', uidProtocolo] as const,
  detail: (uidProtocolo: string, uidAnalisis: string) =>
    [...QUERY_KEY_BASE, uidProtocolo, uidAnalisis] as const,
};

export function useSedimentacionFhListByProtocolo(uidProtocolo: string | undefined) {
  return useQuery({
    queryKey: uidProtocolo
      ? sedimentacionFhKeys.byProtocolo(uidProtocolo)
      : sedimentacionFhKeys.all,
    queryFn: ({ signal }) => sedimentacionFhApi.listByProtocolo(uidProtocolo!, signal),
    enabled: !!uidProtocolo,
  });
}

export function useSedimentacionFh(
  uidProtocolo: string | undefined,
  uidAnalisis: string | undefined
) {
  return useQuery({
    queryKey:
      uidProtocolo && uidAnalisis
        ? sedimentacionFhKeys.detail(uidProtocolo, uidAnalisis)
        : sedimentacionFhKeys.all,
    queryFn: ({ signal }) => sedimentacionFhApi.getById(uidProtocolo!, uidAnalisis!, signal),
    enabled: !!(uidProtocolo && uidAnalisis),
  });
}

export function useCreateSedimentacionFh() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      data,
    }: {
      uidProtocolo: string;
      data: SedimentacionFhCreate;
    }) => sedimentacionFhApi.create(uidProtocolo, data),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({
        queryKey: sedimentacionFhKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}

export function useUpdateSedimentacionFh() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
      data,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
      data: SedimentacionFhUpdate;
    }) => sedimentacionFhApi.update(uidProtocolo, uidAnalisis, data),
    onSuccess: (updated: SedimentacionFh, { uidProtocolo, uidAnalisis }) => {
      void queryClient.invalidateQueries({
        queryKey: sedimentacionFhKeys.byProtocolo(uidProtocolo),
      });
      queryClient.setQueryData(sedimentacionFhKeys.detail(uidProtocolo, uidAnalisis), updated);
    },
  });
}

export function useDeleteSedimentacionFh() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
    }) => sedimentacionFhApi.remove(uidProtocolo, uidAnalisis),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({
        queryKey: sedimentacionFhKeys.byProtocolo(uidProtocolo),
      });
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.detail(uidProtocolo) });
    },
  });
}

/** Hook para subir una foto. Flujo de 3 pasos encapsulado en uploadFoto del API. */
export function useUploadFotoSedimentacionFh() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
      file,
      caption,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
      file: File;
      caption?: string | null;
    }) => sedimentacionFhApi.uploadFoto(uidProtocolo, uidAnalisis, file, caption ?? null),
    onSuccess: (updated: SedimentacionFh, { uidProtocolo, uidAnalisis }) => {
      queryClient.setQueryData(sedimentacionFhKeys.detail(uidProtocolo, uidAnalisis), updated);
      void queryClient.invalidateQueries({
        queryKey: sedimentacionFhKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}

export function useEliminarFotoSedimentacionFh() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uidProtocolo, uidAnalisis, uid_foto }: EliminarFotoVars) =>
      sedimentacionFhApi.eliminarFoto(uidProtocolo, uidAnalisis, uid_foto),
    onSuccess: (updated: SedimentacionFh, { uidProtocolo, uidAnalisis }) => {
      queryClient.setQueryData(sedimentacionFhKeys.detail(uidProtocolo, uidAnalisis), updated);
      void queryClient.invalidateQueries({
        queryKey: sedimentacionFhKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}

export function useEditarCaptionFotoSedimentacionFh() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uidProtocolo, uidAnalisis, uid_foto, caption }: EditarCaptionVars) =>
      sedimentacionFhApi.editarCaption(uidProtocolo, uidAnalisis, uid_foto, caption),
    onSuccess: (updated: SedimentacionFh, { uidProtocolo, uidAnalisis }) => {
      queryClient.setQueryData(sedimentacionFhKeys.detail(uidProtocolo, uidAnalisis), updated);
      void queryClient.invalidateQueries({
        queryKey: sedimentacionFhKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}
